import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Eye, MessagesSquare, FolderClock, Map, Plus, Minus } from "lucide-react";
import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Reveal, MaskedLine, Eyebrow } from "../components/Reveal";
import { EnquiryForm } from "../components/EnquiryForm";
import { Marquee } from "../components/Marquee";
import { IMAGES } from "../siteConfig";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[96vh] items-end overflow-hidden bg-forest-deep">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={IMAGES.hero}
          alt="Aerial view of golden light over green Cotswold fields and woodland"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-forest-deep/75 via-forest-deep/35 to-forest-deep/80" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-6xl px-5 pb-20 pt-44 md:px-8 md:pb-28">
        <MaskedLine delay={0.15}>
          <p className="eyebrow mb-6 flex items-center gap-3 text-ochre">
            <span className="inline-block h-px w-10 bg-ochre" aria-hidden="true" />
            Aerial mapping for rural land &amp; estates
          </p>
        </MaskedLine>
        <h1 className="max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight text-limestone sm:text-5xl lg:text-6xl" data-testid="hero-headline">
          <MaskedLine delay={0.3}>A clearer view</MaskedLine>
          <MaskedLine delay={0.45}>of <span className="italic text-sage">your land.</span></MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-7 max-w-xl text-base leading-relaxed text-limestone/85 md:text-lg"
        >
          Managing land is easier when you can see the bigger picture. Cotswold Drone Mapping turns aerial
          imagery into clear maps and practical visual records, helping landowners, estate managers and rural
          professionals understand their land and plan what happens next.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-primary-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-ochre px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-forest-deep transition-colors hover:bg-limestone"
          >
            Discuss your mapping project
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/services"
            data-testid="hero-secondary-cta"
            className="link-underline text-xs font-semibold uppercase tracking-[0.15em] text-limestone"
          >
            Explore our services
          </Link>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-10 border-l-2 border-ochre/70 pl-4 text-xs uppercase tracking-[0.2em] text-limestone/60"
        >
          Local knowledge. Clear outputs. Mapping shaped around your needs.
        </motion.p>
      </motion.div>
    </section>
  );
};

const Problem = () => (
  <section data-testid="problem-section" className="mx-auto max-w-6xl px-5 py-24 md:px-8 md:py-32">
    <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
      <div>
        <Reveal><Eyebrow>The problem</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-serif text-3xl leading-tight tracking-tight text-forest md:text-4xl lg:text-5xl">
            You can't manage what you can't clearly see.
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-6 text-base leading-relaxed text-charcoal/85">
            When land stretches across fields, woodland, tracks and buildings, it is not always easy to keep
            the whole picture in view. Plans can be out of date. Information can live in different files. And
            explaining a site or a change to someone else can take more time than it should.
          </p>
        </Reveal>
        <Reveal delay={0.26}>
          <ul className="mt-7 space-y-3.5">
            {[
              "Get a clear overview of a property, field or wider estate.",
              "Create a useful visual record of land or site conditions at a point in time.",
              "Help colleagues, contractors or advisers understand a location before work begins.",
              "Compare imagery over time to help monitor visible change or progress.",
              "Bring scattered information together into a clearer, more usable picture.",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-charcoal/85 md:text-base">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-ochre" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.32}>
          <p className="mt-7 font-serif text-lg italic text-forest md:text-xl">
            Cotswold Drone Mapping helps turn that bigger picture into something you can actually use.
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.2} className="relative">
        <div className="absolute -left-3 -top-3 h-full w-full border border-ochre/50" aria-hidden="true" />
        <img
          src={IMAGES.stoneWall}
          alt="Dry stone wall running through a grassy Cotswold field"
          className="relative h-[420px] w-full object-cover lg:h-[540px]"
          loading="lazy"
        />
      </Reveal>
    </div>
  </section>
);

const SERVICES = [
  {
    title: "Estate & land overviews",
    body: "Get a clear aerial view of fields, woodland, access routes and key features, organised to make the wider property easier to understand.",
    image: IMAGES.estate,
    alt: "Aerial view of a country estate and surrounding parkland",
  },
  {
    title: "Aerial imagery & visual records",
    body: "Create a dated visual record of a site or area of land — useful for reference, planning conversations and keeping information together.",
  },
  {
    title: "Field and site mapping",
    body: "Turn suitable aerial imagery into map-based outputs that help you review areas, features and spatial relationships. The scope depends on the site and intended use.",
  },
  {
    title: "Progress & change monitoring",
    body: "Capture imagery at agreed intervals to help compare visible changes, works or site progress over time.",
  },
  {
    title: "Mapping support for rural professionals",
    body: "Aerial imagery and agreed mapping outputs to support surveyors, land managers and other professionals in their work.",
  },
];

const Services = () => (
  <section data-testid="services-section" className="border-y border-forest/10 bg-limestone-dark/50 py-24 md:py-32">
    <div className="mx-auto max-w-6xl px-5 md:px-8">
      <Reveal><Eyebrow>What we do</Eyebrow></Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-5 max-w-2xl font-serif text-3xl leading-tight tracking-tight text-forest md:text-4xl lg:text-5xl">
          Useful mapping. Built around your land.
        </h2>
      </Reveal>
      <Reveal delay={0.18}>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-charcoal/85">
          Every property and project is different. We'll start with what you need to understand, then agree the
          most useful capture and outputs for the job.
        </p>
      </Reveal>

      <div className="mt-14 grid gap-5 md:grid-cols-6">
        {SERVICES.map((s, i) => (
          <Reveal
            key={s.title}
            delay={0.08 * i}
            className={i === 0 ? "md:col-span-4 md:row-span-2" : "md:col-span-2"}
          >
            <Link
              to="/services"
              data-testid={`service-card-${i}`}
              className={`group relative flex h-full min-h-[220px] flex-col justify-end overflow-hidden border border-forest/15 p-7 transition-transform duration-300 hover:-translate-y-1 ${
                i === 0 ? "text-limestone md:min-h-[460px]" : "bg-limestone text-charcoal"
              }`}
            >
              {s.image && (
                <>
                  <img src={s.image} alt={s.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-forest-deep/55" />
                </>
              )}
              <span className="absolute left-7 top-0 h-0.5 w-10 bg-ochre transition-all duration-300 group-hover:w-20" aria-hidden="true" />
              <div className="relative">
                <h3 className={`font-serif text-xl leading-snug md:text-2xl ${i === 0 ? "text-limestone" : "text-forest"}`}>
                  {s.title}
                </h3>
                <p className={`mt-3 text-sm leading-relaxed ${i === 0 ? "max-w-md text-limestone/85" : "text-charcoal/80"}`}>
                  {s.body}
                </p>
              </div>
            </Link>
          </Reveal>
        ))}
        <Reveal delay={0.4} className="md:col-span-2">
          <Link
            to="/contact"
            data-testid="services-cta-card"
            className="group flex h-full min-h-[220px] flex-col justify-between border border-forest bg-forest p-7 text-limestone transition-colors hover:bg-forest-deep"
          >
            <p className="font-serif text-xl italic leading-snug md:text-2xl">Tell us what you need to see</p>
            <ArrowRight className="h-6 w-6 text-ochre transition-transform group-hover:translate-x-1.5" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const BENEFITS = [
  { icon: Eye, title: "See the bigger picture", body: "Understand how fields, woodland, routes and site features sit together." },
  { icon: MessagesSquare, title: "Make conversations clearer", body: "Share a visual reference that helps colleagues, contractors and advisers get on the same page." },
  { icon: FolderClock, title: "Keep a useful record", body: "Create organised, dated imagery that can be referred back to when needed." },
  { icon: Map, title: "Plan with more context", body: "Use a clearer view of the site to support discussions and decisions — alongside the other information and professional advice your project requires." },
];

const Benefits = () => (
  <section data-testid="benefits-section" className="mx-auto max-w-6xl px-5 py-24 md:px-8 md:py-32">
    <Reveal><Eyebrow>Why it helps</Eyebrow></Reveal>
    <Reveal delay={0.1}>
      <h2 className="mt-5 max-w-2xl font-serif text-3xl leading-tight tracking-tight text-forest md:text-4xl lg:text-5xl">
        From aerial images to information you can use.
      </h2>
    </Reveal>
    <div className="mt-14 grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
      {BENEFITS.map((b, i) => (
        <Reveal key={b.title} delay={0.08 * i}>
          <b.icon className="h-7 w-7 text-ochre" strokeWidth={1.25} aria-hidden="true" />
          <h3 className="mt-5 font-serif text-xl text-forest">{b.title}</h3>
          <p className="mt-3 text-sm leading-relaxed text-charcoal/80">{b.body}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const STEPS = [
  { n: "01", title: "Tell us about your land", body: "We'll discuss the location, what you need to understand, how you plan to use the information and any practical constraints." },
  { n: "02", title: "Agree the right approach", body: "We'll confirm the area, capture plan, likely outputs, timescale and scope before work begins." },
  { n: "03", title: "Capture and process", body: "Subject to permissions, conditions and the agreed scope, imagery is captured and processed into the planned outputs." },
  { n: "04", title: "Receive your deliverables", body: "You receive the agreed files and a clear explanation of what they show, along with any important limitations." },
];

const HowItWorks = () => (
  <section data-testid="how-it-works-section" className="border-y border-forest/10 bg-limestone-dark/50 py-24 md:py-32">
    <div className="mx-auto max-w-6xl px-5 md:px-8">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <div>
          <Reveal><Eyebrow>How it works</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 max-w-xl font-serif text-3xl leading-tight tracking-tight text-forest md:text-4xl lg:text-5xl">
              Straightforward from first conversation to final files.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <Link to="/contact" data-testid="how-it-works-cta" className="group inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-forest">
            Start a conversation <ArrowRight className="h-4 w-4 text-ochre transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={0.1 * i} className="border-t border-forest/20 pt-6">
            <span className="font-serif text-4xl italic text-ochre/80">{s.n}</span>
            <h3 className="mt-4 font-serif text-xl text-forest">{s.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-charcoal/80">{s.body}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ExampleTeaser = () => (
  <section data-testid="example-teaser-section" className="bg-forest py-24 text-limestone md:py-32">
    <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 md:px-8 lg:grid-cols-2 lg:gap-20">
      <Reveal className="relative order-2 lg:order-1">
        <img
          src={IMAGES.fields}
          alt="Aerial view of fields arranged around a rural property — illustrative example"
          loading="lazy"
          className="h-[340px] w-full object-cover lg:h-[440px]"
        />
        <span className="absolute left-4 top-4 bg-ochre px-3 py-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-forest-deep">
          Illustrative example
        </span>
      </Reveal>
      <div className="order-1 lg:order-2">
        <Reveal><Eyebrow light>Example work</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-serif text-3xl leading-tight tracking-tight md:text-4xl lg:text-5xl">
            See what a clearer view can look like.
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-6 text-base leading-relaxed text-limestone/80">
            Explore an illustrative example of how aerial imagery and mapping outputs could be organised for a
            rural property. The example demonstrates the format and potential usefulness of the deliverables;
            it is not a real client project.
          </p>
        </Reveal>
        <Reveal delay={0.26}>
          <Link
            to="/example-work"
            data-testid="example-teaser-cta"
            className="group mt-8 inline-flex items-center gap-2 rounded-full border border-limestone/40 px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-limestone transition-colors hover:border-ochre hover:text-ochre"
          >
            View the illustrative example
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const Why = () => (
  <section data-testid="why-section" className="mx-auto max-w-6xl px-5 py-24 md:px-8 md:py-32">
    <div className="max-w-3xl">
      <Reveal><Eyebrow>Why Cotswold Drone Mapping</Eyebrow></Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-5 font-serif text-3xl leading-tight tracking-tight text-forest md:text-4xl lg:text-5xl">
          A practical understanding of rural land.
        </h2>
      </Reveal>
      <Reveal delay={0.18}>
        <p className="mt-6 text-base leading-relaxed text-charcoal/85">
          Cotswold Drone Mapping focuses on the needs of people who manage, own or work with land. The aim is
          not to hand over a folder of images and leave you to work it out. It is to agree what you need to
          see, create clear and organised outputs, and make the information easier to use.
        </p>
      </Reveal>
      <Reveal delay={0.26}>
        <p className="mt-5 text-base leading-relaxed text-charcoal/85">
          Based in the Cotswolds, the service is designed for rural estates, landowners, land managers and
          surveying professionals who value a considered, straightforward approach.
        </p>
      </Reveal>
    </div>
  </section>
);

const FAQS = [
  { q: "What areas do you cover?", a: "Cotswold Drone Mapping is based near Cirencester and focused on the Cotswolds, Gloucestershire, Oxfordshire and Wiltshire. Share your site location and we can discuss whether the project is practical." },
  { q: "What can you map?", a: "Depending on the site and agreed scope, projects may include estate overviews, fields, woodland edges, access routes, site imagery and visual records of change or progress." },
  { q: "Can you map a large estate?", a: "Potentially, yes. The right approach depends on the area, terrain, access, airspace and permissions, required outputs, weather and project timescale. We'll discuss the scope before confirming feasibility." },
  { q: "Can the maps be used to establish legal boundaries?", a: "Aerial imagery and mapping outputs should not be treated as a legal determination of property boundaries. Where legal boundaries or formal survey measurements are required, consult an appropriately qualified land surveyor or legal professional." },
  { q: "How much does it cost?", a: "Costs depend on the site, area, capture requirements, processing and deliverables. Tell us what you need and we can discuss a suitable scope and quotation." },
  { q: "What will I receive?", a: "Deliverables are agreed in advance and may include aerial imagery, map-based outputs and a concise visual report. The exact format and level of detail depend on the project." },
];

const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="faq-section" className="border-t border-forest/10 py-24 md:py-32">
      <div className="mx-auto max-w-3xl px-5 md:px-8">
        <Reveal><Eyebrow>Questions</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-serif text-3xl leading-tight tracking-tight text-forest md:text-4xl">
            Asked often, answered plainly.
          </h2>
        </Reveal>
        <div className="mt-12">
          {FAQS.map((f, i) => (
            <Reveal key={f.q} delay={0.05 * i} className="border-b border-forest/15">
              <button
                data-testid={`faq-toggle-${i}`}
                onClick={() => setOpen(open === i ? -1 : i)}
                aria-expanded={open === i}
                className="flex w-full items-center justify-between gap-6 py-6 text-left"
              >
                <span className="font-serif text-lg text-forest md:text-xl">{f.q}</span>
                {open === i ? <Minus className="h-5 w-5 shrink-0 text-ochre" /> : <Plus className="h-5 w-5 shrink-0 text-ochre" />}
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <p className="pb-6 pr-10 text-sm leading-relaxed text-charcoal/80 md:text-base">{f.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const FinalCta = () => (
  <section data-testid="final-cta-section" className="mx-auto max-w-6xl px-5 py-24 md:px-8 md:py-32">
    <Reveal><Eyebrow>Start here</Eyebrow></Reveal>
    <Reveal delay={0.1}>
      <h2 className="mt-5 max-w-2xl font-serif text-3xl leading-tight tracking-tight text-forest md:text-4xl lg:text-5xl">
        What would you like a <span className="italic">clearer view</span> of?
      </h2>
    </Reveal>
    <Reveal delay={0.18}>
      <p className="mt-6 max-w-xl text-base leading-relaxed text-charcoal/85">
        Tell us a little about your land and what you're trying to understand. We'll talk through the options
        and whether aerial mapping is the right fit for your project.
      </p>
    </Reveal>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Problem />
      <Services />
      <Benefits />
      <HowItWorks />
      <ExampleTeaser />
      <Why />
      <Faq />
      <FinalCta />
      <section data-testid="home-contact-section" className="border-t border-forest/10 bg-limestone-dark/50 py-24 md:py-32">
        <div className="mx-auto grid max-w-6xl gap-14 px-5 md:px-8 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <Reveal><Eyebrow>Contact</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-serif text-3xl leading-tight tracking-tight text-forest md:text-4xl">
                Tell us about your project
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-6 text-base leading-relaxed text-charcoal/85">
                A few details will help us understand what you need. You don't need to know the technical
                terminology — we can work through that together.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-3">
            <EnquiryForm />
          </Reveal>
        </div>
      </section>
      <Marquee />
    </>
  );
}
